import { useEffect, useMemo, useRef } from "react";
import useAnimateWhileVisible from "../../hooks/useAnimateWhileVisible";
import "./AtomField.css";

// Brand colors — CSS custom properties, so dots auto-update with the palette.
const COLORS = ["var(--gold)", "var(--gold-bright)", "var(--cyan)", "var(--coral)", "var(--coral-bright)"];

// Chain-reaction "generations": each wave pops in with one more dot than the
// last, spreading further from the center every time.
const GENERATIONS = [2, 3, 4, 5, 6, 6];
const GEN_DELAY = 0.45; // seconds between each generation's pop-in
const SEED = 20261010;
const PATH_POINTS = 180; // keyframes per drift loop: steps under 7px, turns under 6°

// Seeded, so the field is the same on every visit (and in every screenshot),
// still scattered: it never looks like a grid.
function mulberry32(a) {
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* Each dot has a DEPTH (0 = far, 1 = near) that sets everything else
   together, so the field reads as layers over the background: far dots are
   smaller, dimmer, drift less and more slowly; near dots are larger,
   brighter and a little quicker. The ranges are the original ones (5-12px,
   opacity 0.35-0.70, 40-130px of drift): the dots are not bigger, brighter
   or busier than before, only consistent with each other.

   Movement: a closed, smooth loop (a Lissajous figure: two sideways cycles
   for every three up-and-down, or the other way round). Sideways and
   vertical speeds peak and ease off at different moments, so a dot glides,
   slows and turns without ever stopping dead, and the loop's end meets its
   start exactly: no jump when it repeats. */
export function buildAtoms(rand = mulberry32(SEED)) {
  const atoms = [];
  let i = 0;
  GENERATIONS.forEach((count, gen) => {
    const radius = 4 + gen * 8; // % distance from center, grows per generation
    for (let n = 0; n < count; n++) {
      const angle = (n / count) * Math.PI * 2 + gen * 0.6;
      const r = radius + (rand() - 0.5) * 6;
      const x = 50 + Math.cos(angle) * r;
      const y = 50 + Math.sin(angle) * r;
      const depth = rand();
      const reach = 40 + depth * 90;                       // px of drift
      const driftAngle = rand() * Math.PI * 2;
      const period = 22 - depth * 10;                      // s: far 22, near 12
      const swap = rand() < 0.5;
      const phase = rand() * Math.PI * 2;
      const popDelay = gen * GEN_DELAY + rand() * 0.2;

      atoms.push({
        id: i,
        left: `${Math.min(98, Math.max(2, x))}%`,
        top: `${Math.min(98, Math.max(2, y))}%`,
        size: 5 + depth * 7,
        opacity: (0.35 + depth * 0.35).toFixed(2),
        color: COLORS[i % COLORS.length],
        // The loop: half-widths along x and y keep the dot's main drift
        // direction, with enough of the other axis that it curves.
        ax: 0.5 * reach * (0.4 + 0.6 * Math.abs(Math.cos(driftAngle))),
        ay: 0.5 * reach * (0.4 + 0.6 * Math.abs(Math.sin(driftAngle))),
        cyclesX: swap ? 3 : 2,
        cyclesY: swap ? 2 : 3,
        phase,
        loop: period * 2.4,                                // s for one full loop
        offset: rand(),                                    // start part-way round
        pop: `${popDelay.toFixed(2)}s`,
      });
      i++;
    }
  });
  return atoms;
}

// Concrete pixel values only: an animation of `transform` with no CSS
// variables runs on the compositor, off the main thread.
export function driftKeyframes(a) {
  const frames = [];
  for (let k = 0; k <= PATH_POINTS; k++) {
    const t = (k / PATH_POINTS) * Math.PI * 2;
    const x = a.ax * Math.sin(a.cyclesX * t + a.phase);
    const y = a.ay * Math.sin(a.cyclesY * t);
    // "+ 0" turns -0 into 0, so the loop's last frame is exactly its first.
    const px = (v) => (Math.round(v * 10) / 10 + 0).toFixed(1);
    frames.push({ transform: `translate(${px(x)}px, ${px(y)}px)` });
  }
  return frames;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const AtomField = () => {
  const atoms = useMemo(() => buildAtoms(), []);
  const ref = useRef(null);
  const animations = useRef([]);
  // Paused offscreen and in a hidden tab, like every other endless
  // animation since speed Phase 0 (useAnimateWhileVisible).
  const animate = useAnimateWhileVisible(ref);

  useEffect(() => {
    const field = ref.current;
    // Reduced motion: no drift at all, the dots simply stay where they are.
    if (!field || prefersReducedMotion()) return undefined;
    const nodes = field.querySelectorAll(".atom-drift");
    animations.current = atoms.map((a, i) => {
      const el = nodes[i];
      if (!el || typeof el.animate !== "function") return null;
      const duration = a.loop * 1000;
      return el.animate(driftKeyframes(a), {
        duration,
        delay: -a.offset * duration,
        iterations: Infinity,
        easing: "linear",
      });
    }).filter(Boolean);
    return () => {
      animations.current.forEach((anim) => anim.cancel());
      animations.current = [];
    };
  }, [atoms]);

  useEffect(() => {
    animations.current.forEach((anim) => (animate ? anim.play() : anim.pause()));
  }, [animate]);

  return (
    <div ref={ref} className={`atom-field${animate ? "" : " atom-field--paused"}`} aria-hidden="true" data-testid="atom-field">
      {atoms.map(a => (
        <span key={a.id} className="atom-drift" style={{ left: a.left, top: a.top }}>
          <span
            className="atom"
            style={{
              width: `${a.size.toFixed(1)}px`,
              height: `${a.size.toFixed(1)}px`,
              color: a.color,
              "--op": a.opacity,
              "--glow": `${(a.size * 0.9).toFixed(1)}px`,
              animationDelay: a.pop,
            }}
          />
        </span>
      ))}
    </div>
  );
};

export default AtomField;
